// The app's one StationData, and React hooks over its status. Set once at startup (main.tsx);
// null until then, or when the local database can't be opened (the shell still renders and
// says so through the sync indicator).

import type { StationData, StationDataStatus } from '@enp-suite/data';
import { useSyncExternalStore } from 'react';

let current: StationData | null = null;
const listeners = new Set<() => void>();

export function provideStationData(data: StationData | null): void {
  current = data;
  for (const l of listeners) l();
}

export function getStationData(): StationData | null {
  return current;
}

/**
 * Calls `onChange` when the StationData is set or its status changes. Every call is its own
 * subscription, even for a function already subscribed (the sync status passes one shared
 * callback for every component that shows it): its unsubscribe ends only this one.
 */
export function subscribeStationData(onChange: () => void): () => void {
  const changed = () => onChange();
  let unsubscribeStatus = current?.status.subscribe(changed);
  const onProvide = () => {
    unsubscribeStatus?.();
    unsubscribeStatus = current?.status.subscribe(changed);
    changed();
  };
  listeners.add(onProvide);
  return () => {
    listeners.delete(onProvide);
    unsubscribeStatus?.();
    unsubscribeStatus = undefined;
  };
}

export function getStationDataStatus(): StationDataStatus | null {
  return current?.status.get() ?? null;
}

/** The data layer's status (pairing, gate, entitlement, clock, session, engine), or null. */
export function useStationDataStatus(): StationDataStatus | null {
  return useSyncExternalStore(subscribeStationData, getStationDataStatus, () => null);
}
